/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { convertTimestampToISOString } from '@/features/customReviews/utils/convertTimestampToISOString';
import type {
  AggregateRating,
  ProductReviewsData,
  ReviewSchema,
  ReviewsSchema,
} from '../interface';
import type { ProductModel } from '@/api/types/products-types';

const bestRating = 5;

export function generateReviewsList(reviewsData: ProductReviewsData): ReviewSchema[] {
  return reviewsData.reviews.map((review) => ({
    '@type': 'Review',
    dateCreated: convertTimestampToISOString(review.submittedTimestamp),
    headline: review.title,
    reviewBody: review.body,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating,
    },
    author: {
      '@type': 'Person',
      name: review.reviewer ?? 'Anonymous',
    },
    image: review.images.length
      ? review.images.map((image) => ({
          '@type': 'ImageObject',
          image: image.normalPhotoUrl,
          caption: image.photoCaption ?? undefined,
          description: 'normal',
          thumbnail: {
            '@type': 'ImageObject',
            name: image.thumbPhotoUrl,
          },
        }))
      : undefined,
  }));
}

export function generateAggregateRating(reviewsData: ProductReviewsData): AggregateRating {
  return {
    '@type': 'AggregateRating',
    bestRating,
    ratingValue: reviewsData.averageRating,
    ratingCount: reviewsData.totalReviewCount,
    reviewCount: reviewsData.totalReviewCount,
  };
}

export function generateReviewsSchema(
  productData: ProductModel,
  reviewsData: ProductReviewsData
): ReviewsSchema {
  const {
    itemId,
    partNumber,
    brandName,
    itemImageUrl,
    itemDescription,
    marketingCopy,
    featuresAndBenefits,
  } = productData;

  const description = (marketingCopy ?? '') || (featuresAndBenefits?.join(' ') ?? '');

  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    description,
    sku: itemId,
    mpn: partNumber,
    image: itemImageUrl,
    name: itemDescription,
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    aggregateRating: generateAggregateRating(reviewsData),
    reviews: generateReviewsList(reviewsData),
  };
}
